import type { VerificationTableVersions } from "@sitescene/types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";

/** Wire shape of `POST /shadow/plausibility/seed`. */
export interface SeedReport {
	discriminators: number;
	/** Global specs published from the Taxonomy page, left alone. */
	discriminatorsSkipped?: number;
	dryRun: boolean;
	priors: number;
	/** Seed priors left alone because a person edited the row. */
	priorsSkipped: number;
	taxonomy: number;
	/** Classes published from the Taxonomy page, left alone. */
	taxonomySkipped?: number;
	versions: VerificationTableVersions;
}

/**
 * Write the in-code seeds into the admin database. Idempotent; each real run
 * bumps the table version counters. A dry run only counts what would be
 * written and reads the current versions.
 */
export const useSeed = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: { dryRun: boolean }) => {
			const response = await apiClient.post<SeedReport>(
				ENDPOINTS.shadow.plausibility.seed,
				input,
			);
			return response.data;
		},
		onSuccess: async (report) => {
			if (report.dryRun) return;
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.plausibility.globalPriors,
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.plausibility.taxonomy,
				}),
			]);
		},
	});
};
